import React from 'react';
import type { Lead } from '../../../../types';
import { isPdfDocument, type LeadDocument } from '../../../projects/utils/leadDocumentFiles';
import type { LeadActivity, LeadEmailEntry, NoteHistoryEntry } from '../../../../pages/LeadDetailPage';
import type { LeadChatHistoryItem } from './useLeadProjectChats';
import type { Task } from '../../../aufgaben/types';

export type HistoryFilter = 'all' | 'activities' | 'notes' | 'emails' | 'files' | 'chats' | 'tasks';

/** Documents uploaded with outbound mail (offer/invoice send) land within seconds of the email row. */
const EMAIL_LINKED_DOC_WINDOW_MS = 60_000;

function emailTimestampMs(em: LeadEmailEntry): number {
  return new Date(em.date_at || em.created_at).getTime();
}

/** Prefer docs whose filename / document number appears in the subject (e.g. AN-26-00018-01). */
function filenameMatchesSubject(filename: string, subject: string): boolean {
  const sub = subject.toLowerCase();
  const refs = filename.match(/[A-Z]{1,5}-\d{2}-\d{4,}(?:-\d+)?/gi) || [];
  if (refs.some((ref) => sub.includes(ref.toLowerCase()))) return true;
  const base = filename.replace(/\.[^.]+$/, '').trim();
  if (base.length < 6) return false;
  return sub.includes(base.toLowerCase());
}

function linkDocumentsToEmails(
  emails: LeadEmailEntry[],
  documents: LeadDocument[],
): { linkedByEmailId: Map<string, LeadDocument[]>; claimedDocIds: Set<number> } {
  const linkedByEmailId = new Map<string, LeadDocument[]>();
  const claimedDocIds = new Set<number>();
  if (emails.length === 0 || documents.length === 0) {
    return { linkedByEmailId, claimedDocIds };
  }

  const emailsByTime = [...emails].sort((a, b) => emailTimestampMs(a) - emailTimestampMs(b));
  const docsByTime = [...documents].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );

  for (const em of emailsByTime) {
    const emMs = emailTimestampMs(em);
    const subject = em.subject || '';
    const candidates = docsByTime.filter((doc) => {
      if (claimedDocIds.has(doc.id)) return false;
      const delta = Math.abs(new Date(doc.created_at).getTime() - emMs);
      return delta <= EMAIL_LINKED_DOC_WINDOW_MS;
    });
    if (candidates.length === 0) continue;

    const preferred = candidates.filter((doc) => filenameMatchesSubject(doc.filename, subject));
    // Sicherheitsnetz: Wenn keine Datei-Referenz im Subject-Pattern erkennbar ist,
    // verknüpfen wir keine „zufälligen“ Dokumente aus dem Zeitfenster.
    if (preferred.length === 0) continue;

    const chosen = preferred.sort(
      (a, b) =>
        Math.abs(new Date(a.created_at).getTime() - emMs) -
        Math.abs(new Date(b.created_at).getTime() - emMs),
    );

    // Claim the closest match(es) that share the same second-ish upload burst for this email.
    const firstMs = new Date(chosen[0].created_at).getTime();
    const linked: LeadDocument[] = [];
    for (const doc of chosen) {
      if (Math.abs(new Date(doc.created_at).getTime() - firstMs) > 5_000) continue;
      linked.push(doc);
      claimedDocIds.add(doc.id);
    }
    if (linked.length > 0) linkedByEmailId.set(String(em.id), linked);
  }

  return { linkedByEmailId, claimedDocIds };
}

function buildHistoryFeedEntries({
  lead,
  activities,
  noteHistory,
  leadEmails,
  documents,
  chats,
  tasks,
  isLeadLost,
  historyFilter,
}: {
  lead: Lead;
  activities: LeadActivity[];
  noteHistory: NoteHistoryEntry[];
  leadEmails: LeadEmailEntry[];
  documents: LeadDocument[];
  chats: LeadChatHistoryItem[];
  tasks: Task[];
  isLeadLost: boolean;
  historyFilter: HistoryFilter;
}) {
  const { linkedByEmailId, claimedDocIds } = linkDocumentsToEmails(leadEmails, documents);
  const standaloneDocuments =
    historyFilter === 'files'
      ? documents
      : documents.filter((d) => !claimedDocIds.has(d.id));

  const feedEntries = [
    { type: 'created' as const, date: lead.created_at },
    ...activities.map((a) => ({ type: 'activity' as const, date: a.due_at || a.created_at, ...a })),
    ...noteHistory.map((n) => ({ type: 'note' as const, date: n.saved_at, ...n })),
    ...leadEmails.map((em) => ({
      type: 'email' as const,
      date: em.date_at || em.created_at,
      ...em,
      linkedDocuments: linkedByEmailId.get(String(em.id)) ?? [],
    })),
    ...standaloneDocuments.map((d) => ({ type: 'file' as const, date: d.created_at, ...d })),
    ...chats.map((c) => ({
      type: 'chat' as const,
      date: c.updatedAt,
      chatId: c.chatId,
      title: c.title,
    })),
    // Erledigte zum Erledigt-Zeitpunkt, offene zum Anlege-Zeitpunkt
    ...tasks.map((t) => ({ type: 'task' as const, date: t.done_at ?? t.created_at, task: t })),
    ...(isLeadLost && (lead.loss_reason != null || lead.lost_at != null)
      ? [{ type: 'lost' as const, date: lead.lost_at || lead.created_at }]
      : []),
  ]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .filter((e) => {
      if (historyFilter === 'all') return true;
      if (historyFilter === 'activities') return e.type === 'activity';
      if (historyFilter === 'notes') return e.type === 'note';
      if (historyFilter === 'emails') return e.type === 'email';
      if (historyFilter === 'files') return e.type === 'file';
      if (historyFilter === 'chats') return e.type === 'chat';
      if (historyFilter === 'tasks') return e.type === 'task';
      return false;
    });
  // Bilder/Dateien (keine PDFs), die innerhalb von 60 Sekunden hochgeladen wurden, zu einer Karte zusammenfassen
  type FeedEntry = (typeof feedEntries)[number];
  type FileEntry = Extract<FeedEntry, { type: 'file' }>;
  type FileGroup = { type: 'file-group'; date: string; docs: FileEntry[] };
  type GroupedEntry = FeedEntry | FileGroup;
  const FILE_GROUP_WINDOW_MS = 60_000;
  const grouped: GroupedEntry[] = [];
  // Offene Gruppe wird über Zeit-Nähe zur zuletzt hinzugefügten Datei fortgeführt (chronologisch benachbart).
  // Ein zwischenliegendes PDF behält seine eigene Karte, unterbricht aber den Upload-Stapel nicht.
  let currentGroup: FileGroup | null = null;
  for (const entry of feedEntries) {
    if (entry.type === 'file') {
      // PDFs behalten ihre eigene Karte und werden nicht als Vorschau-Raster gruppiert
      if (isPdfDocument(entry)) {
        grouped.push(entry);
        continue;
      }
      const adjacent = currentGroup?.docs[currentGroup.docs.length - 1];
      if (
        currentGroup &&
        adjacent &&
        Math.abs(
          new Date(adjacent.created_at).getTime() -
            new Date(entry.created_at).getTime(),
        ) <= FILE_GROUP_WINDOW_MS
      ) {
        currentGroup.docs.push(entry);
        continue;
      }
      currentGroup = { type: 'file-group', date: entry.date, docs: [entry] };
      grouped.push(currentGroup);
      continue;
    }
    // Jeder Nicht-Datei-Eintrag beendet den laufenden Upload-Stapel
    currentGroup = null;
    grouped.push(entry);
  }
  return grouped;
}

export type LeadHistoryFeedEntry = ReturnType<typeof buildHistoryFeedEntries>[number];

/** Aggregiert Aktivitäten/Notizen/E-Mails/Dateien/Verloren-Ereignis zu einem sortierten, gefilterten und gruppierten Verlaufs-Feed. */
export function useLeadHistoryEntries(params: {
  lead: Lead | null;
  activities: LeadActivity[];
  noteHistory: NoteHistoryEntry[];
  leadEmails: LeadEmailEntry[];
  documents: LeadDocument[];
  chats: LeadChatHistoryItem[];
  tasks: Task[];
  isLeadLost: boolean;
  historyFilter: HistoryFilter;
}): LeadHistoryFeedEntry[] {
  const { lead, activities, noteHistory, leadEmails, documents, chats, tasks, isLeadLost, historyFilter } = params;
  return React.useMemo(
    () =>
      lead
        ? buildHistoryFeedEntries({
            lead,
            activities,
            noteHistory,
            leadEmails,
            documents,
            chats,
            tasks,
            isLeadLost,
            historyFilter,
          })
        : [],
    [lead, activities, noteHistory, leadEmails, documents, chats, tasks, isLeadLost, historyFilter],
  );
}
